import { BdayLogo } from "@/components/bday-logo";
import { BUILD_VERSION } from "@/lib/build-info";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UserAvatar } from "@/components/user-avatar";
import { useAuth } from "@/hooks/use-auth";
import { HAD_SESSION_AT_BOOT } from "@/lib/session-hint";
import { returnHereSearch } from "@/site/components/auth/auth-utils";
import { HEADER_PATTERN } from "@/lib/patterns";
import { cn } from "@/lib/utils";
import { CategoryNav } from "@/site/components/category-nav";
import { useCardNav } from "@/site/components/category-nav-data";
import { NotificationBell } from "@/site/components/notifications/notification-bell";
import { Menu as MenuPrimitive } from "@base-ui/react/menu";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Menu as MenuIcon, X as XIcon } from "lucide-react";
import { useRef, useState } from "react";

type NavLink = {
  label: string;
  to?: string;
  search?: Record<string, unknown>;
  comingSoon?: boolean;
  onClick?: () => void;
  /** A rule above the item — the old menu's `<Divider>` before the admin block. */
  dividerAbove?: boolean;
  /** Open in a new tab (the old admin link's `target="_blank"`). */
  newTab?: boolean;
  /** The old `.admin-link.red` styling. */
  red?: boolean;
  /** Old parity: the mobile menu never showed the admin block — desktop dropdown only. */
  desktopOnly?: boolean;
};

type NavMenu = { label: string; items: NavLink[]; to?: string };

function useLoggedInMenus(): NavMenu[] {
  const { user, logout } = useAuth();
  return [
    {
      label: "My Reminders",
      to: "/reminders",
      items: [
        { label: "My Reminders", to: "/reminders" },
        { label: "My Contacts", to: "/address_book" },
        { label: "My Calendar", to: "/calendar" },
      ],
    },
    {
      label: "My Cards",
      to: "/mycards/upcoming",
      items: [
        { label: "Browse Cards", to: "/cards" },
        // Old top-menu My Cards dropdown: one entry per /mycards tab.
        { label: "Upcoming", to: "/mycards/upcoming" },
        { label: "Sent", to: "/mycards/sent" },
        { label: "Signed", to: "/mycards/signed" },
        { label: "Received", to: "/mycards/received" },
        { label: "Drafts", to: "/mycards/drafts" },
        { label: "Favorites", to: "/mycards/favorites" },
        { label: "Photo", to: "/mycards/photo" },
        { label: "Gift Cards", to: "/mycards/gift" },
      ],
    },
    {
      label: "My Account",
      to: "/account",
      items: [
        // The five PRD 08 account surfaces, at their legacy URLs (mirrors the old
        // top-menu: Account / Email / Notifications / Change Password / Payment Info).
        { label: "Account Settings", to: "/account" },
        { label: "Email Settings", to: "/email_settings" },
        { label: "Notifications", to: "/notifications" },
        { label: "Change Password", to: "/change_password" },
        { label: "Payment Info", to: "/payment" },
        // NOT part of the legacy five above: PRD 11 D10 makes /support the member's
        // "track your tickets" view, and it needs a way in from the account area.
        // (Opening a NEW ticket stays on /contact, the public front door.)
        { label: "My Tickets", to: "/support" },
        // `void`: a rejected logout is reported by the mutation's own onError toast
        // (use-auth), so the handler must not leave an unhandled promise behind.
        { label: "Sign Out", onClick: () => void logout() },
        // Old top-menu admin block: a divider below Sign Out, then a red "Admin" link
        // opening the console in a new tab, desktop dropdown only (`{isAdmin && ...}`).
        // /admin is the Overview landing (PRD 15.2, 2026-10-27); it used to be /admin/users
        // because the console had no index route.
        ...(user?.is_admin
          ? [
              {
                label: "Admin",
                to: "/admin",
                dividerAbove: true,
                newTab: true,
                red: true,
                desktopOnly: true,
              },
            ]
          : []),
      ],
    },
  ];
}

// Shared base for the top-level desktop nav links (logged-out Reminders / Sign In /
// Register), mirroring the old .top-menu-item: full-height, bold, uppercase.
const navItemClass =
  "inline-flex h-full items-center px-4 text-sm font-bold uppercase text-foreground";

// Matches old .top-menu-dropdown .MuiMenuItem-root: semibold, 10px 25px,
// rounded 6px, green_hover bg + white text on hover.
const itemClass =
  "block cursor-pointer whitespace-nowrap rounded-[6px] px-[25px] py-[10px] text-sm font-semibold text-foreground outline-none hover:bg-primary-hover hover:text-white data-[highlighted]:bg-primary-hover data-[highlighted]:text-white";

function MenuItemLink({ item }: { item: NavLink }) {
  // Old menu's `<Divider>` before the admin block — rendered as a sibling rule.
  const divider = item.dividerAbove ? (
    <div aria-hidden className="mx-2 my-1 border-t border-grey-line" />
  ) : null;
  if (item.comingSoon) {
    return (
      <MenuPrimitive.Item
        className={`${itemClass} cursor-default text-foreground/50`}
        disabled
        data-coming-soon
      >
        {item.label}
      </MenuPrimitive.Item>
    );
  }
  if (item.onClick) {
    return (
      <MenuPrimitive.Item onClick={item.onClick} className={itemClass}>
        {item.label}
      </MenuPrimitive.Item>
    );
  }
  return (
    <>
      {divider}
      <MenuPrimitive.Item
        render={
          <Link
            to={item.to!}
            search={item.search}
            {...(item.newTab ? { target: "_blank", rel: "noopener" } : {})}
          />
        }
        // cn (twMerge) drops itemClass's conflicting text-foreground — appending the class as
        // a plain string leaves the winner to stylesheet order, which picks foreground.
        className={item.red ? cn(itemClass, "text-destructive") : itemClass}
      >
        {item.label}
      </MenuPrimitive.Item>
    </>
  );
}

// One state, one timer. Opening any menu instantly closes the others (the controlled
// `open` prop is the single source of truth; the menu only ever reports closes back).
// Leaving any zone schedules a close; the next enter cancels it.
function useHoverNav() {
  const [openId, setOpenId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return {
    openId,
    open: (id: string) => {
      if (timer.current) clearTimeout(timer.current);
      setOpenId(id);
    },
    close: () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setOpenId(null), 100);
    },
  };
}

type HoverNav = ReturnType<typeof useHoverNav>;

function NavMenuButton({
  menu,
  trailing,
  nav,
}: {
  menu: NavMenu;
  trailing?: React.ReactNode;
  nav: HoverNav;
}) {
  const onEnter = () => nav.open(menu.label);
  const navigate = useNavigate();
  const isOpen = nav.openId === menu.label;
  // A finger has no hover, so its first tap opens the dropdown and a second tap on the label
  // goes to the section; decided per gesture (not per device) so a touch laptop or an iPad
  // with a trackpad serves both. Legacy only worked on touch by Safari's hover emulation.
  const touchTapOnClosed = useRef(false);
  const onPointerDown = (e: React.PointerEvent) => {
    touchTapOnClosed.current = e.pointerType === "touch" && !isOpen;
  };
  // Old top-menu behavior: hovering opens the dropdown; CLICKING the label navigates
  // to the section's landing page (MY CARDS -> /mycards/upcoming, etc.).
  const onClick = () => {
    if (touchTapOnClosed.current) {
      touchTapOnClosed.current = false;
      onEnter();
      return;
    }
    if (menu.to) {
      nav.close();
      void navigate({ to: menu.to });
    } else {
      onEnter();
    }
  };
  const hoverHandlers = { onMouseEnter: onEnter, onMouseLeave: nav.close };
  return (
    <MenuPrimitive.Root
      open={isOpen}
      modal={false}
      // Closes also come from the menu (outside tap, Escape, item chosen); opens stay ours.
      onOpenChange={(open) => {
        if (!open) nav.close();
      }}
    >
      <MenuPrimitive.Trigger
        render={
          <button
            type="button"
            {...hoverHandlers}
            onPointerDown={onPointerDown}
            onClick={onClick}
            className="inline-flex h-full cursor-pointer items-center gap-2 px-4 text-sm font-bold uppercase text-foreground"
          />
        }
      >
        <span>{menu.label}</span>
        {trailing}
      </MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Positioner sideOffset={0} align="center">
          <MenuPrimitive.Popup
            {...hoverHandlers}
            className="rounded-[6px] bg-white p-1 outline-none shadow-[0_1px_3px_rgba(0,0,0,0.12),0_1px_2px_rgba(0,0,0,0.24)]"
          >
            {menu.items.map((item) => (
              <MenuItemLink key={item.label} item={item} />
            ))}
            {menu.label === "My Account" && (
              <div
                className="mt-1 border-t border-border/60 px-3 py-1.5 text-[11px] text-muted-foreground select-text"
                title="Build version"
              >
                {BUILD_VERSION}
              </div>
            )}
          </MenuPrimitive.Popup>
        </MenuPrimitive.Positioner>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}

// Verbatim port of FcKey from react-icons/fc — the colorful flat-color key
// the old codebase used as the admin avatar indicator. Two-tone (#FFA000 +
// #D68600), 48x48 viewBox, rendered at the size passed in via className.
function FcKey(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden {...props}>
      <g fill="#FFA000">
        <polygon points="30,41 26,45 22,45 18,41 18,21 30,21 30,29 28,31 30,33 30,35 28,37 30,39" />
        <path d="M38,7.8C37.5,6,36,4.7,34.3,4.2C31.9,3.7,28.2,3,24,3s-7.9,0.7-10.3,1.2C12,4.7,10.5,6,10,7.8 c-0.5,1.7-1,4.1-1,6.7c0,2.6,0.5,5,1,6.7c0.5,1.8,1.9,3.1,3.7,3.5C16.1,25.3,19.8,26,24,26s7.9-0.7,10.3-1.2 c1.8-0.4,3.2-1.8,3.7-3.5c0.5-1.7,1-4.1,1-6.7C39,11.9,38.5,9.5,38,7.8z M29,13H19c-1.1,0-2-0.9-2-2V9c0-0.6,3.1-1,7-1s7,0.4,7,1v2 C31,12.1,30.1,13,29,13z" />
      </g>
      <rect x="23" y="26" fill="#D68600" width="2" height="19" />
    </svg>
  );
}

function AccountAvatar() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <span className="relative ml-[10px] mr-[-15px] inline-flex">
      <UserAvatar
        avatarUrl={user.avatar_url}
        name={user.display_name ?? user.username}
        className="h-[35px] w-[35px] border-2 border-white"
        fallbackClassName="bg-[#2e2e2e]! text-white! text-xs font-bold uppercase"
      />
      {user.is_admin && <FcKey className="absolute -bottom-[5px] -right-[5px] h-5 w-5" />}
    </span>
  );
}

function Divider() {
  return (
    <span aria-hidden className="select-none text-xl text-tan">
      |
    </span>
  );
}

// Mirrors the old .mobile-menu-title: uppercase bold, padding 8px 20px,
// full-width clickable row.
// Row geometry mirrors the old `.mobile-menu-title` nested in a MUI ListItem:
// 8px+8px vertical padding (53px row) and 16px+20px left inset (text at 36px).
const mobileItemClass =
  "block w-full cursor-pointer px-9 py-4 text-left text-base leading-[21px] font-bold uppercase text-foreground no-underline hover:bg-muted";
const mobileSubItemClass =
  "block w-full cursor-pointer px-5 py-2 text-left text-sm font-semibold uppercase text-foreground no-underline hover:bg-grey-faint";

function MobileCollapseSection({ menu, onNavigate }: { menu: NavMenu; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-5 py-2 text-left text-base font-bold uppercase text-foreground hover:bg-muted"
      >
        <span>{menu.label}</span>
        {open ? (
          <ChevronUp className="h-[30px] w-[30px]" />
        ) : (
          <ChevronDown className="h-[30px] w-[30px]" />
        )}
      </button>
      {open && (
        <ul className="bg-[#f7f7f7] px-5 py-1">
          {/* Old parity: the mobile menu never carried the admin block. */}
          {menu.items
            .filter((item) => !item.desktopOnly)
            .map((item) => (
              <li key={item.label} className="list-none">
                {item.comingSoon ? (
                  <span
                    className={`${mobileSubItemClass} cursor-default text-foreground/50 hover:bg-transparent`}
                    data-coming-soon
                    title={`${item.label} — coming soon`}
                  >
                    {item.label}
                  </span>
                ) : item.onClick ? (
                  <button
                    type="button"
                    onClick={() => {
                      item.onClick?.();
                      onNavigate();
                    }}
                    className={mobileSubItemClass}
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    to={item.to!}
                    search={item.search}
                    onClick={onNavigate}
                    className={mobileSubItemClass}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
        </ul>
      )}
    </li>
  );
}

export function SiteHeader({ simpleChrome: forceSimple = false }: { simpleChrome?: boolean } = {}) {
  const { user, softUser, isLoading } = useAuth();
  // A returning member's page paints before `/me` answers (routes/__root.tsx): keep
  // the nav empty until it does, rather than flashing Sign In / Register at them.
  const awaitingMember = isLoading && HAD_SESSION_AT_BOOT;
  const [mobileOpen, setMobileOpen] = useState(false);
  // Only the open mobile menu uses these, so defer the fetch until then (the
  // cards-route category bar fetches the same query separately).
  const { data: navCategories = [] } = useCardNav({ enabled: mobileOpen });
  const loggedIn = !!user;
  // A SOFT (reminder email-link) member: greeted by name ONLY in the simple header
  // (legacy showSimpleHeader). Everywhere else they get the plain logged-out nav —
  // Reminders | Sign In | Register, no name — because every menu destination is
  // full-auth (PRD 08 D18) and "Sign In" elevates and returns here.
  // 2026-10-21 Nick: legacy parity — dropped the off-simple-page "Welcome {name}"
  // and the soft-only Register hide (desktop + mobile); legacy showed Register to
  // every non-full-auth visitor.
  const welcomeName = (user ?? softUser)?.display_name ?? "Guest";
  const [remindersMenu, cardsMenu, accountMenu] = useLoggedInMenus();
  const nav = useHoverNav();

  // The category bar appears only on the homepage and the card-browser routes,
  // mirroring the old `withCardNav = pathname === "/" || pathname.startsWith("/cards")`.
  // Notably NOT on /home (MemberHome) or other pages.
  const { pathname } = useLocation();
  const showCategoryNav =
    pathname === "/" || pathname === "/cards" || pathname.startsWith("/cards/");

  // Old `showSimpleHeader` (hooks/app): logo + "Welcome {name || Guest}" only, no
  // menus, shown to EVERYONE (anonymous, soft and fully signed-in) on these paths.
  // 2026-10-21 Nick: legacy parity — list extended to the full legacy set, mapped
  // to v2 routes; the v2-only surfaces (/s/, /dob/, /cards/browse) stay.
  const simpleChrome =
    // The email-verification intercept (`OnboardingShell`) sits at whatever path the
    // member was heading to, so it asks for the simple bar explicitly.
    forceSimple ||
    // Card surfaces: create, read, share, sign, and /card/send/* except success.
    pathname === "/card/create" ||
    pathname.startsWith("/card/read/") ||
    pathname.startsWith("/card/share/") ||
    pathname.startsWith("/card/sign/") ||
    (pathname.startsWith("/card/send/") && !pathname.startsWith("/card/send/success")) ||
    // Group signing (legacy /birthday/group; v2 sign link /s/<token>).
    pathname === "/birthday/group" ||
    pathname.startsWith("/s/") ||
    // Birthday Request: legacy /birthday/request/* except success + howto. v2's
    // responder is /dob/<token>; /bday|bd4-7/<magic> are the legacy magic hand-offs.
    pathname.startsWith("/dob/") ||
    /^\/(bday|bd[4-7])\//.test(pathname) ||
    (pathname.startsWith("/birthday/request/") &&
      pathname !== "/birthday/request/success" &&
      pathname !== "/birthday/request/howto") ||
    // Reminder-email browse flow.
    pathname === "/birthday/browse" ||
    pathname === "/anniversary/browse" ||
    pathname === "/holiday/browse" ||
    pathname === "/cards/browse" ||
    // Standalone legacy landings.
    pathname === "/unsubscribe" ||
    pathname === "/happy_birthday" ||
    pathname === "/name" ||
    pathname.startsWith("/name/") ||
    pathname === "/infographic" ||
    pathname === "/anniversary/gift" ||
    // The auth wall — legacy put /login, /register and /forgot in the same
    // `showSimpleHeader` list.
    pathname === "/login" ||
    pathname === "/register" ||
    pathname === "/forgot";
  if (simpleChrome) {
    return (
      <header
        className="relative z-[2] flex h-[65px] items-center justify-between px-[25px] shadow-[inset_0_-2px_5px_rgba(0,0,0,0.1)]"
        style={{ backgroundImage: `url("${HEADER_PATTERN}")`, backgroundRepeat: "repeat" }}
        data-testid="site-header-simple"
      >
        {/* pb-[5px] mirrors the old inline-anchor baseline gap that floats the logo
            ~2px above center. */}
        <Link
          to={loggedIn ? "/home" : "/"}
          aria-label="BirthdayAlarm — home"
          className="-ml-4 block px-2 pb-[5px]"
          data-testid="header-logo"
        >
          <BdayLogo size={31} aria-hidden />
        </Link>
        {/* Old simple header hid the welcome text on mobile (no room next to logo). */}
        <span className="hidden px-4 text-sm font-bold text-foreground uppercase sm:inline">
          Welcome {welcomeName}
        </span>
      </header>
    );
  }

  return (
    <>
      <header
        className="relative z-[2] flex h-[65px] items-center justify-between px-[25px] shadow-[inset_0_-2px_5px_rgba(0,0,0,0.1)]"
        style={{ backgroundImage: `url("${HEADER_PATTERN}")`, backgroundRepeat: "repeat" }}
        data-testid="site-header"
      >
        {/* Same destinations as the old app (logged-out → `/`, logged-in → /home)
            but linked directly so logged-in members skip the `/`→/home bounce.
            Direct `/` visits are still handled by routes/index.tsx's redirect. */}
        {/* pb-[5px]: same inline-anchor baseline gap as the simple header's logo. */}
        <Link
          to={loggedIn ? "/home" : "/"}
          aria-label="BirthdayAlarm — home"
          className="-ml-4 block px-2 pb-[5px]"
          data-testid="header-logo"
        >
          <BdayLogo size={31} aria-hidden />
        </Link>

        {awaitingMember ? (
          <nav aria-label="Primary" className="hidden h-full items-center md:flex" />
        ) : loggedIn ? (
          <nav aria-label="Primary" className="hidden h-full items-center md:flex">
            <NotificationBell mode="popover" />
            <Divider />
            <NavMenuButton menu={remindersMenu!} nav={nav} />
            <Divider />
            <NavMenuButton menu={cardsMenu!} nav={nav} />
            <Divider />
            <NavMenuButton menu={accountMenu!} nav={nav} trailing={<AccountAvatar />} />
          </nav>
        ) : (
          <nav aria-label="Primary" className="hidden h-full items-center md:flex">
            {/* Logged-out "Reminders" goes to the public marketing landing
                (/reminders/add), matching legacy. Signed-in users get "My Reminders"
                in the account menu; the auth-gated /reminders list isn't linked here. */}
            <Link to="/reminders/birthday/add" className={navItemClass}>
              Reminders
            </Link>
            <Divider />
            <Link
              to="/login"
              search={returnHereSearch()}
              className={`${navItemClass} hover:text-foreground/80`}
              data-testid="button-nav-login"
            >
              Sign In
            </Link>
            <Divider />
            <Link
              to="/register"
              className={`${navItemClass} hover:text-foreground/80`}
              data-testid="button-nav-register"
            >
              Register
            </Link>
          </nav>
        )}

        <div className="flex items-center md:hidden">
          {loggedIn && <NotificationBell mode="sheet" />}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger
              render={
                <button
                  type="button"
                  aria-label="Open menu"
                  className="-mr-4 ml-1 inline-flex h-10 w-10 cursor-pointer items-center justify-center"
                >
                  <MenuIcon className="h-8 w-8" />
                </button>
              }
            />
            {/* showCloseButton off — the SheetHeader below carries its own X (the
                built-in one stacked a second X in the same corner). */}
            <SheetContent
              side="left"
              showCloseButton={false}
              className="gap-0 max-w-80 p-0 data-[side=left]:w-[83.333%]"
            >
              <SheetHeader className="flex flex-row items-center justify-between px-5 py-3">
                <SheetTitle className="text-left">
                  <BdayLogo size={31} aria-hidden />
                </SheetTitle>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMobileOpen(false)}
                  className="-mr-[10px] inline-flex h-10 w-10 cursor-pointer items-center justify-center"
                >
                  <XIcon className="h-[30px] w-[30px]" />
                </button>
              </SheetHeader>
              <div className="border-t border-divider" />
              <ul className="flex flex-col py-1">
                {loggedIn ? (
                  [accountMenu, remindersMenu, cardsMenu].map((menu) =>
                    menu ? (
                      <MobileCollapseSection
                        key={menu.label}
                        menu={menu}
                        onNavigate={() => setMobileOpen(false)}
                      />
                    ) : null,
                  )
                ) : (
                  <>
                    <li className="list-none">
                      <Link
                        to="/login"
                        search={returnHereSearch()}
                        onClick={() => setMobileOpen(false)}
                        className={mobileItemClass}
                      >
                        Sign In
                      </Link>
                    </li>
                    <li className="list-none">
                      <Link
                        to="/register"
                        onClick={() => setMobileOpen(false)}
                        className={mobileItemClass}
                      >
                        Register
                      </Link>
                    </li>
                    <li className="list-none">
                      <Link
                        to="/reminders/birthday/add"
                        onClick={() => setMobileOpen(false)}
                        className={mobileItemClass}
                      >
                        Reminders
                      </Link>
                    </li>
                  </>
                )}
                {/* Grey section header (old `.mobile-menu-section-title`): surface-grey
                  background, 1rem 2.2rem padding, normal weight, pulled up 0.5rem. */}
                <li className="-mt-2 list-none bg-surface-grey px-[2.2rem] py-4 text-base leading-[21px] uppercase text-foreground">
                  Browse Cards
                </li>
                <li className="list-none">
                  <Link
                    to="/cards"
                    onClick={() => setMobileOpen(false)}
                    className={mobileItemClass}
                  >
                    All Cards
                  </Link>
                </li>
                {navCategories.map((c) => (
                  <li key={c.label} className="list-none">
                    <Link
                      to="/cards/$"
                      params={{ _splat: c.path }}
                      onClick={() => setMobileOpen(false)}
                      className={mobileItemClass}
                    >
                      {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </SheetContent>
          </Sheet>
        </div>
      </header>
      {showCategoryNav && <CategoryNav />}
    </>
  );
}
